import { ReactElement } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Alert, Button } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useIsAnyActionBlocked } from '@editorHooks'
import { getResultsValid, type ResultsValidityResponse } from '@queries'

/**
 * Warns that generating the document may fail for items relying on calculation
 * data while the results are missing or invalid.
 */
const ResultsValidityWarning = (): ReactElement | null => {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { isBlockedFn: isAnyActionBlockedFn } = useIsAnyActionBlocked()

  // Same query as the process navigation's results state, so this reads its cache
  const { data: resultsValid, isLoading } = useQuery<ResultsValidityResponse | null, AxiosError>({
    queryKey: getResultsValid.getKey(projectId ?? ''),
    queryFn: () => getResultsValid.request(projectId),
    enabled: Boolean(projectId),
    onError: error =>
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Prüfen der Ergebnisse'), {
        variant: 'error',
        preventDuplicate: true,
      }),
  })

  // Partially valid results are good enough, only missing or invalid ones warn
  if (isLoading || resultsValid?.fully_valid || resultsValid?.partially_valid) return null

  return (
    <Alert
      severity="warning"
      data-cy="document-generation-results-warning"
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            if (isAnyActionBlockedFn()) {
              enqueueSnackbar(
                'Bitte speichern Sie Ihre Änderungen, bevor Sie die Seite wechseln.',
                {
                  variant: 'warning',
                  preventDuplicate: true,
                },
              )
              return
            }
            navigate(`/editor/${projectId}/results`)
          }}
        >
          Zur Ergebnisansicht
        </Button>
      }
    >
      Die Ergebnisse sind nicht aktuell. Die Erzeugung kann fehlschlagen, wenn Elemente verwendet
      werden, die Berechnungsdaten benötigen (z. B. Nachweise). Führen Sie gegebenenfalls zuerst die
      Berechnung aus.
    </Alert>
  )
}

export default ResultsValidityWarning
